import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function EnquiryForm({ property }) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", preferred_contact: "",
    preferred_viewing_date: "", message: "", buying_position: "",
    mortgage_aip: "", consent: false, marketing_consent: false, honeypot: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const params = new URLSearchParams(window.location.search);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please provide consent so we can process your enquiry.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/enquiries", {
        ...form,
        property_id: property?.id,
        page_url: window.location.href,
        referring_page: document.referrer,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
      });
      setDone(true);
      toast.success(res.data.message || "Enquiry received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit enquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="enquiry-success">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-green-600" />
        </div>
        <h3 className="mt-4 font-display text-xl font-bold text-[#111827]">Enquiry received</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
          Thank you. Your details will be passed to the relevant property partner, who may contact you directly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
      <input type="text" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={(e) => set("honeypot", e.target.value)} className="hidden" aria-hidden />
      <div className="grid grid-cols-1 gap-4">
        <Field label="Full name" required><Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="enquiry-name" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Email" required><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="enquiry-email" /></Field>
          <Field label="Phone" required><Input required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="enquiry-phone" /></Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Preferred contact">
            <Select value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)}>
              <SelectTrigger data-testid="enquiry-contact-method"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="Phone">Phone</SelectItem>
                <SelectItem value="Either">Either</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Preferred viewing date"><Input type="date" value={form.preferred_viewing_date} onChange={(e) => set("preferred_viewing_date", e.target.value)} data-testid="enquiry-date" /></Field>
        </div>
        <Field label="Your position">
          <Select value={form.buying_position} onValueChange={(v) => set("buying_position", v)}>
            <SelectTrigger data-testid="enquiry-position"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="First-time buyer">First-time buyer</SelectItem>
              <SelectItem value="Buying to let">Buying to let</SelectItem>
              <SelectItem value="Chain-free">Chain-free</SelectItem>
              <SelectItem value="In a chain">In a chain</SelectItem>
              <SelectItem value="Tenant">Tenant looking to rent</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Message"><Textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="I'd like to arrange a viewing..." data-testid="enquiry-message" /></Field>
      </div>

      <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
        <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" data-testid="enquiry-consent" />
        <span>I consent to Homio sharing my details with the relevant property partner so they can respond to my enquiry. <span className="text-[#C81D35]">*</span></span>
      </label>
      <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
        <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => set("marketing_consent", !!v)} className="mt-0.5" data-testid="enquiry-marketing" />
        <span>I'd like to receive occasional property updates and news (optional).</span>
      </label>

      <Button type="submit" disabled={submitting} className="w-full h-11 rounded-full bg-[#C81D35] hover:bg-[#a5162b] font-semibold" data-testid="enquiry-submit">
        {submitting ? "Sending..." : "Submit Enquiry"}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        The relevant agency or property partner may contact you directly regarding this property.
      </p>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-[#111827]">{label} {required && <span className="text-[#C81D35]">*</span>}</Label>
      {children}
    </div>
  );
}
